import { getClassroomDetails } from "@/js/api/classApi";
import { getUserDetail, searchUsers } from "@/js/api/usersApi";
import { ClassroomInviteCode } from "@/js/components/InviteCode/InviteCode";
import useStore from "@/stores/index";
import { getFeatures } from "@/stores/classwizeConfigStore";
import { compareChangesInArrays } from "@/js/utils/arrayUtils";
import { getUserDisplayTextForSelect } from "@/js/utils/userUtils";
import React, { useEffect, useMemo, useState } from "react";
import { CLASSROOM_SOURCE_TYPES, CLASSROOM_TYPES } from "@/types/classrooms";
import * as clsApi from "../../api/classroomApi";
import { useLicenseStore } from "@/stores/v2/licenseStore";
import { EDIT_CLASSROOM_ACTION } from "../ClassroomTileV2";
import ErrorBoundary from "@/components/ErrorBoundary";
import { DangerButton } from "../shared/button/DangerButton";
import { PrimaryButton } from "../shared/button/PrimaryButton";
import { DeleteClassConfirmModal } from "@/components/DeleteClassConfirmModal";
import StandardModalV2 from "../shared/StandardModalV2";
import { renderTeacherTab } from "./teacherTab";
import { Box, Button, Spinner } from "@chakra-ui/react";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { getClassroomDisplayName } from "@/utils/classroomUtils";
import { ClassroomOverviewDTO, ClassroomStudentDTO, ClassroomTeacherDTO, ModifyClassDTO, UserDetail } from "@/stores/classroomStore_I";
import { UserInfo } from "@/js/api/authApi";

export interface ClassroomEditV2Props {
	/** The classroom object containing details about the classroom. */
	classroom: ClassroomOverviewDTO;
	/** Indicates whether the modal is open. */
	open: boolean;
	/** Callback function to handle actions like save, cancel, or delete. */
	handleAction: (action: string) => void;
}

export const ClassroomEditV2 = (props: ClassroomEditV2Props): JSX.Element => {
	const features = getFeatures();
	const [hasError, setHasError] = useState<string | false>(false);
	const [isDeleteOpen, setDeleteOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isInviteCodeEnabled, setInviteCodeEnabled] = useState(!!props.classroom.session?.inviteCode);

	const [students, setStudents] = useState<ClassroomStudentDTO[]>([]);
	const [stagedStudents, setStagedStudents] = useState<ClassroomStudentDTO[]>([]); // This should actually be removed now, modifying students is managed in a separate modal, but I don't have capacity to clean this up right now.
	const [teachers, setTeachers] = useState<ClassroomTeacherDTO[]>([]);
	const [stagedTeachers, setStagedTeachers] = useState<ClassroomTeacherDTO[]>([]);
	const [stagedInvitation, setStagedInvitation] = useState({
		code: props.classroom.session?.inviteCode,
		expiry: props.classroom.session?.inviteCodeExpiry,
	});
	const [teacherError, setTeacherError] = useState<string>();
	const [lastSelectedTeacher, setLastSelectedTeacher] = useState<UserDetail>();

	const { classroom } = useLicenseStore();

	const TABS = useMemo(() => {
		const result: { [key: string]: string } = {
			// Tabs that are always shown, regardless of licensed products
			teacher: "Teachers",
		};
		if (classroom) {
			// Tabs that are shown only if Classroom Management is licensed
			result.invitation = "Invitation Code";
		}
		return result;
	}, [classroom]);

	const [activeTab, setActiveTab] = useState(TABS.teacher);

	useEffect(() => {
		getClassroomDetails(
			props.classroom.id || (props.classroom.name as string),
			true,
			true,
			false,
			props.classroom.type,
			props.classroom.sourceId,
			useUserSessionStore.getState().getTeacherId() as string
		).then((classroomDetail) => {
			const { classroom } = classroomDetail;
			// @ts-expect-error - Leaving as it was in the original code
			useStore.getState().classroom.updateClassroomDataV2(classroomDetail);
			setStudents(classroom.students || []);
			setStagedStudents(classroom.students || []);
			setTeachers(classroom.teachers || []);
			setStagedTeachers(classroom.teachers || []);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderActions = () => {
		const renderDelete = () => {
			const { classroom } = props;
			const sourceType = classroom.sourceType.toLowerCase();
			if (classroom.type.toLowerCase() === CLASSROOM_TYPES.CLASSROOM && sourceType.toLowerCase() === CLASSROOM_SOURCE_TYPES.LOCAL) {
				return (
					<DangerButton
						className="mui-flat-button"
						label="Delete class"
						primary={false}
						secondary={true}
						onClick={() => handleAction(EDIT_CLASSROOM_ACTION.delete)}
						data-cy="delete"
					/>
				);
			}

			return <span />;
		};

		const renderSave = () => {
			if (isSaving) {
				return (
					<ErrorBoundary>
						<PrimaryButton className="mui-flat-button" label="" primary={true}>
							<Spinner color="white" width={"12px"} height={"12px"} />
						</PrimaryButton>
					</ErrorBoundary>
				);
			}
			const isButtonDisabled = !!hasError;
			return (
				<Button onClick={() => handleAction(EDIT_CLASSROOM_ACTION.save)} data-cy="save" isDisabled={isButtonDisabled}>
					Save
				</Button>
			);
		};

		return (
			<div className="ClassroomEdit_modalButtons">
				{renderDelete()}
				{renderSave()}
			</div>
		);
	};

	const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
		if (!hasChanged(stagedStudents, students) && !hasChanged(stagedTeachers, teachers)) {
			props.handleAction(EDIT_CLASSROOM_ACTION.cancel);
		}

		setIsSaving(true);
		if (event) event.preventDefault();

		const { classroom } = props;
		const updatedClassroom = {
			group: classroom.sourceId,
			groupId: classroom.id,
			classroomUsers: stagedStudents.map((user) => user.username),
			classroomUserIds: stagedStudents.map((user) => user.id),
			classroomOwners: stagedTeachers.map((user) => user.username),
			classroomOwnerIds: stagedTeachers.map((user) => user.id),
			invite_code: stagedInvitation?.code,
			invite_code_expiry: stagedInvitation?.expiry,
			is_monitoring_class: classroom.session?.isMonitoring,
		};

		updateClassroomDetails(classroom.type, updatedClassroom);
	};

	const isClassroomValid = async (classroomType: string, updatedClassroom: ModifyClassDTO) => {
		if (!classroomType) {
			setHasError("Classroom type is required");
			return false;
		}

		if (!updatedClassroom) {
			setHasError("Modified classroom is required");
			return false;
		}

		setHasError(false);
		return true;
	};

	const updateClassroomDetails = async (classroomType: string, updatedClassroom: ModifyClassDTO) => {
		// @ts-expect-error - Leaving as it was in the original code
		if (isClassroomValid(classroomType, updatedClassroom)) {
			updatedClassroom = {
				...updatedClassroom,
				// @ts-expect-error - Leaving as it was in the original code
				policy_id: props.classroom.stableIdV1,
			};
			const endpoint = classroomType.toLowerCase() === CLASSROOM_TYPES.CLASSROOM ? "classrooms/" : "groups/";
			await clsApi
				.Post(
					endpoint + updatedClassroom.groupId,
					updatedClassroom,
					() => {
						useStore.getState().classroom.updateClassroomDataV2(updatedClassroom, true);
						handleAction(EDIT_CLASSROOM_ACTION.saveComplete);
						setHasError(false);
					},
					(error) => {
						// @ts-expect-error - Leaving as it was in the original code
						setHasError(error);
					}
				)
				.finally(() => {
					setIsSaving(false);
				});
		}
	};

	const handleAction = async (action: string) => {
		switch (action) {
			case EDIT_CLASSROOM_ACTION.cancel:
			case EDIT_CLASSROOM_ACTION.saveComplete:
				setDeleteOpen(false);
				props.handleAction(action);
				break;
			case EDIT_CLASSROOM_ACTION.delete:
				setDeleteOpen(true);
				break;
			case EDIT_CLASSROOM_ACTION.save:
				setDeleteOpen(false);
				// @ts-expect-error - Leaving as it was in the original code
				handleSave();
				break;
			case EDIT_CLASSROOM_ACTION.deleteConfirmed:
				setDeleteOpen(false);
				props.handleAction(EDIT_CLASSROOM_ACTION.delete);
				break;
			default:
				break;
		}
	};

	const handleSwitchTab = (tab: string) => {
		setActiveTab(tab);
	};

	const getTabClassname = (tab: string) => {
		if (activeTab === tab) {
			return "ClassroomEdit_modalTabButton--selected";
		}
		return "ClassroomEdit_modalTabButton";
	};

	const getTabContent = (classroom: ClassroomOverviewDTO) => {
		switch (activeTab) {
			case TABS.teacher: {
				return renderTeacherTab(
					classroom,
					lastSelectedTeacher as UserDetail | null,
					teacherError as string | null,
					getUsersCb as () => Promise<{ text: string; value: UserInfo }[]>,
					stagedTeachers,
					handleAddClassroomOwnerCb,
					handleRemoveTeacher,
					() => setTeacherError(undefined)
				);
			}
			case TABS.invitation:
				return renderInvitationContent();
			default:
				return null;
		}
	};

	const renderTitleSection = () => {
		return (
			<div className="ClassroomEdit_modalHeader">
				<div className="ClassroomEdit_modalTabContainer">
					{Object.entries(TABS).map(([key, value]) => {
						// Only show invitation tab if the classroom is LOCAL
						if (value === TABS.invitation && props.classroom.sourceType.toLowerCase() !== CLASSROOM_SOURCE_TYPES.LOCAL) {
							return <></>;
						}
						return (
							<div className={getTabClassname(value)} key={key} onClick={() => handleSwitchTab(value)}>
								{value}
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	const getUsersCb = async (filterStr: string): Promise<{ text: string; value: UserInfo }[]> => {
		if (filterStr) {
			const students = await searchUsers(filterStr);
			return students.map((user) => ({
				text: getUserDisplayTextForSelect(user),
				value: user,
			}));
		}
		return [];
	};

	const handleAddClassroomOwner = async (owner: UserInfo) => setStagedTeachers([...stagedTeachers, owner]);

	const handleAddClassroomOwnerCb = async (user: UserInfo) => {
		//Get teacher details first
		try {
			//Check for duplicates
			if (stagedTeachers.some((previous) => user.username === previous.username)) {
				setTeacherError(getUserDisplayTextForSelect(user) + " is already a teacher of the class, please select a different user");
				return;
			}

			//Perform search
			const foundUser = await getUserDetail(user.username, false, false, true);
			setLastSelectedTeacher(foundUser);
			if (!foundUser) {
				setTeacherError(getUserDisplayTextForSelect(user) + " is not a valid teacher, please try a different user");
				return;
			}

			//First check if this is a teacher, otherwise bail
			if (!foundUser.isTeacher) {
				return;
			}

			handleAddClassroomOwner(user);
		} catch (error) {
			console.error("Error fetching user details", error);
			setTeacherError(getUserDisplayTextForSelect(user) + " is not a valid teacher, please try a different user");
		}
	};

	const handleRemoveTeacher = (user: UserInfo) => {
		// @ts-expect-error - Leaving as it was in the original code
		const tmpClassroomTeachers = stagedTeachers.filter((teacher) => getUserDisplayTextForSelect(teacher) !== user);
		setStagedTeachers(tmpClassroomTeachers);
	};

	const renderInvitationContent = () => (
		<div>
			<ClassroomInviteCode
				codeInviteEnabled={isInviteCodeEnabled}
				// @ts-expect-error - Leaving as it was in the original code
				code={stagedInvitation?.code || props.classroom.session?.inviteCode}
				// @ts-expect-error - Leaving as it was in the original code
				codeExpiry={stagedInvitation?.expiry || props.classroom.session?.inviteCodeExpiry}
				codeCallback={(enabled, code, expiry) => {
					setInviteCodeEnabled(enabled);
					setStagedInvitation({ code, expiry });
				}}
				studentCount={stagedStudents?.length}
			/>
		</div>
	);

	function hasChanged<T>(current: T[], previous: T[]) {
		return current.length && compareChangesInArrays(current, previous) > 0;
	}

	const renderContentSection = () => {
		const { classroom } = props;
		if (features.indexOf("add_user") > -1) {
			return getTabContent(classroom);
		} else {
			return <div>Cannot edit students or teachers. This classroom has been automatically created by the administrator.</div>;
		}
	};

	const renderModalBody = () => {
		return (
			<div>
				{renderTitleSection()}
				<br />
				{props.classroom && renderContentSection()}
			</div>
		);
	};

	const getDescription = () => {
		const classroomName = props.classroom.type === "CLASSROOM" ? props.classroom.name : props.classroom.description;
		// @ts-expect-error - Leaving as it was in the original code
		if (props.classroom?.personalisation?.displayName?.length > 0) {
			return classroomName;
		}
		return null;
	};

	return (
		<Box onClick={(event) => event.stopPropagation()}>
			<StandardModalV2
				title={`Class settings - ${getClassroomDisplayName(props.classroom)}`}
				description={getDescription()}
				actions={renderActions()}
				// @ts-expect-error - Leaving as it was in the original code
				modal={true}
				open={props.open}
				close={() => handleAction(EDIT_CLASSROOM_ACTION.cancel)}
				bodyClass="classSettings_modal"
			>
				{renderModalBody()}
				<DeleteClassConfirmModal
					open={isDeleteOpen}
					classroomName={getClassroomDisplayName(props.classroom)}
					onDelete={() => handleAction(EDIT_CLASSROOM_ACTION.deleteConfirmed)}
					onCancel={() => setDeleteOpen(false)}
				/>
			</StandardModalV2>
		</Box>
	);
};
